/**
 * Engine âm thanh B3: nhạc/SFX tổng hợp bằng code (WebAudio).
 *
 * - 4 bản nhạc generative gieo hạt được (xem nhac-sang-tac.ts) + 20 SFX
 *   (xem hieu-ung.ts): TUYỆT ĐỐI không có file audio thật — đúng triết lý
 *   "asset 100% bằng code", 0 rủi ro license (quyết định B3).
 * - Logic sáng tác/định nghĩa tách hàm thuần (test bằng vitest/node);
 *   engine chỉ render các sự kiện thuần ra WebAudio.
 * - Tắt nhạc / tắt SFX RIÊNG (master-plan 3.5 Tiếp cận).
 * - AudioContext chỉ tạo sau cử chỉ người dùng (chính sách autoplay).
 *   Bài học idle-2d A6: phatNhac() gọi TRƯỚC moKhoa() phải GHI NHỚ ý định
 *   (nhacDangPhat) — moKhoa() sẽ khởi động track đang chờ, nếu không nhạc
 *   câm vĩnh viễn.
 * - Inject được bộ tạo context để test (không cần browser).
 */

import { taoRng } from '../core/rng.js';
import {
  chonTrack,
  laTrackNhac,
  soanNhac,
  type BanNhac,
  type IdTrackNhac,
  type NotNhac,
} from './nhac-sang-tac.js';
import { dinhNghiaSfx, type SuKienNhieu, type SuKienOsc, type SuKienSfx } from './hieu-ung.js';

/** Giao diện tối thiểu của AudioContext mà engine dùng (để fake trong test). */
export interface BoTaoAudio {
  taoContext(): AudioContext | null;
}

/** Id SFX mưa (ambient lặp vòng). */
const SFX_MUA = 'sfx-mua';

export class EngineAmThanh {
  private ctx: AudioContext | null = null;
  private readonly boTao: BoTaoAudio;
  private nhacBat = true;
  private sfxBat = true;
  /** Track đang phát HOẶC đang chờ (gọi phatNhac trước moKhoa). */
  private nhacDangPhat: IdTrackNhac | null = null;
  /** true khi vòng lặp nhạc đã được lên lịch trên ctx hiện tại. */
  private lichNhacDangChay = false;
  private henGioNhac: number | null = null;
  /** Token vô hiệu hóa lịch cũ khi đổi/dừng track. */
  private theHeNhac = 0;
  /** Ambient mưa: lặp vòng sfx-mua. */
  private muaDangRoi = false;
  private lichMuaDangChay = false;
  private henGioMua: number | null = null;
  private theHeMua = 0;
  /** Bộ đệm nhiễu dùng chung (sinh deterministic, cache 1 lần). */
  private boDemNhieu: AudioBuffer | null = null;
  /** true khi không có WebAudio (test/SSR) — mọi lệnh thành no-op an toàn. */
  readonly khaDung: boolean;

  constructor(boTao?: BoTaoAudio) {
    this.boTao =
      boTao ??
      ({
        taoContext: () => {
          if (typeof window === 'undefined') return null;
          const AC =
            window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
          if (!AC) return null;
          return new AC();
        },
      } satisfies BoTaoAudio);
    this.khaDung = typeof window !== 'undefined' && 'AudioContext' in window;
  }

  /**
   * Gọi sau cử chỉ người dùng đầu tiên để mở khóa autoplay.
   * Khởi động track nhạc / ambient mưa đang chờ (bài học A6).
   */
  moKhoa(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
    } else {
      try {
        this.ctx = this.boTao.taoContext();
      } catch {
        this.ctx = null;
      }
    }
    if (!this.ctx) return;
    if (this.nhacDangPhat && this.nhacBat && !this.lichNhacDangChay) {
      this.batDauLichNhac(this.nhacDangPhat);
    }
    if (this.muaDangRoi && this.sfxBat && !this.lichMuaDangChay) {
      this.batDauLichMua();
    }
  }

  datNhacBat(bat: boolean): void {
    this.nhacBat = bat;
    if (!bat) this.dungNhac();
  }

  datSfxBat(bat: boolean): void {
    this.sfxBat = bat;
    if (!bat) this.dungMua();
  }

  /** Track đang phát hoặc đang chờ mở khóa (null = không có). */
  dangPhatNhac(): string | null {
    return this.nhacDangPhat;
  }

  /**
   * Phát SFX theo định nghĩa thuần. No-op khi tắt SFX, chưa mở khóa,
   * hoặc id lạ.
   */
  phatSfx(id: string): void {
    const dn = dinhNghiaSfx(id);
    if (!dn || !this.sfxBat || !this.ctx) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime + 0.01;
    for (const sk of dn.suKien) this.renderSuKien(ctx, sk, t0);
  }

  /**
   * Phát nhạc nền. Đổi track thì dừng track cũ.
   *
   * QUAN TRỌNG (bài học A6): gọi trước moKhoa() chỉ ghi nhớ ý định vào
   * nhacDangPhat — moKhoa() sẽ khởi động track đang chờ. Không bao giờ
   * "quên" track khiến nhạc câm vĩnh viễn.
   */
  phatNhac(id: string): void {
    if (!laTrackNhac(id)) return;
    if (this.nhacDangPhat === id && this.lichNhacDangChay) return;
    this.dungNhac();
    if (!this.nhacBat) return;
    this.nhacDangPhat = id; // ghi nhớ ý định, kể cả khi chưa mở khóa
    if (!this.ctx) return; // chờ moKhoa() khởi động
    this.batDauLichNhac(id);
  }

  /** Chọn + phát track theo khu vực và giờ game (thuần ở chonTrack). */
  datNhacTheoKhuVaGio(khuId: string, gio: number): void {
    this.phatNhac(chonTrack(khuId, gio));
  }

  /**
   * Bật/tắt ambient mưa (lặp vòng sfx-mua). Gọi khi thời tiết đổi.
   * Tôn trọng tắt SFX: tắt SFX thì mưa cũng tắt.
   */
  datMuaDangRoi(coMua: boolean): void {
    this.muaDangRoi = coMua;
    if (!coMua || !this.sfxBat) {
      this.dungMua();
      return;
    }
    if (!this.ctx || this.lichMuaDangChay) return;
    this.batDauLichMua();
  }

  /** Dừng nhạc nền (và xóa ý định đang chờ). */
  dungNhac(): void {
    this.theHeNhac += 1;
    this.lichNhacDangChay = false;
    this.nhacDangPhat = null;
    if (this.henGioNhac !== null) {
      clearTimeout(this.henGioNhac);
      this.henGioNhac = null;
    }
  }

  /** Hủy engine (khi unmount). */
  huy(): void {
    this.dungNhac();
    this.dungMua();
    if (this.ctx) {
      void this.ctx.close().catch(() => undefined);
      this.ctx = null;
    }
  }

  // --- Nội bộ ---

  /** Lên lịch một vòng lặp nhạc rồi hẹn vòng tiếp theo (token-guarded). */
  private batDauLichNhac(id: IdTrackNhac): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const ban: BanNhac = soanNhac(id);
    const t0 = ctx.currentTime + 0.05;
    for (const n of ban.not) this.renderNot(ctx, n, t0);
    for (const nh of ban.nhieu) this.renderNhieu(ctx, nh, t0);
    this.theHeNhac += 1;
    const theHe = this.theHeNhac;
    this.lichNhacDangChay = true;
    this.henGioNhac = globalThis.setTimeout(() => {
      if (this.theHeNhac === theHe && this.nhacDangPhat === id && this.ctx) {
        this.batDauLichNhac(id);
      } else {
        this.lichNhacDangChay = false;
      }
    }, ban.tongThoiGian * 1000) as unknown as number;
  }

  /** Lên lịch vòng lặp mưa. */
  private batDauLichMua(): void {
    const ctx = this.ctx;
    const dn = dinhNghiaSfx(SFX_MUA);
    if (!ctx || !dn) return;
    const t0 = ctx.currentTime + 0.05;
    for (const sk of dn.suKien) this.renderSuKien(ctx, sk, t0);
    this.theHeMua += 1;
    const theHe = this.theHeMua;
    this.lichMuaDangChay = true;
    this.henGioMua = globalThis.setTimeout(() => {
      if (this.theHeMua === theHe && this.muaDangRoi && this.sfxBat && this.ctx) {
        this.batDauLichMua();
      } else {
        this.lichMuaDangChay = false;
      }
    }, dn.tongThoiGian * 1000) as unknown as number;
  }

  private dungMua(): void {
    this.theHeMua += 1;
    this.lichMuaDangChay = false;
    if (this.henGioMua !== null) {
      clearTimeout(this.henGioMua);
      this.henGioMua = null;
    }
  }

  private renderSuKien(ctx: AudioContext, sk: SuKienSfx, t0: number): void {
    if (sk.loai === 'osc') this.renderOsc(ctx, sk, t0);
    else this.renderNhieu(ctx, sk, t0);
  }

  private renderNot(ctx: AudioContext, n: NotNhac, t0: number): void {
    this.renderOsc(
      ctx,
      {
        loai: 'osc',
        batDau: n.batDau,
        thoiLuong: n.thoiLuong,
        tanSoDau: n.tanSo,
        tanSoCuoi: n.tanSo,
        kieuSong: n.kieuSong,
        doDay: n.doDay,
      },
      t0,
    );
  }

  private renderOsc(ctx: AudioContext, sk: SuKienOsc, t0: number): void {
    const t = t0 + sk.batDau;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = sk.kieuSong;
    osc.frequency.setValueAtTime(Math.max(1, sk.tanSoDau), t);
    if (sk.tanSoCuoi !== sk.tanSoDau) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, sk.tanSoCuoi), t + sk.thoiLuong);
    }
    const tanCong = Math.min(0.03, sk.thoiLuong * 0.2);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, sk.doDay), t + tanCong);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + sk.thoiLuong);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + sk.thoiLuong + 0.05);
  }

  private renderNhieu(ctx: AudioContext, sk: SuKienNhieu, t0: number): void {
    const t = t0 + sk.batDau;
    const nguon = ctx.createBufferSource();
    nguon.buffer = this.layBoDemNhieu(ctx);
    nguon.loop = true;
    const loc = ctx.createBiquadFilter();
    loc.type = 'lowpass';
    loc.frequency.setValueAtTime(Math.max(40, sk.tanSoLoc), t);
    if (sk.tanSoLocCuoi !== sk.tanSoLoc) {
      loc.frequency.exponentialRampToValueAtTime(Math.max(40, sk.tanSoLocCuoi), t + sk.thoiLuong);
    }
    const gain = ctx.createGain();
    if (sk.dangBao === 'song') {
      // Dâng lên rồi rút (sóng biển, gió, mưa rào).
      const tanLen = sk.thoiLuong * 0.35;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(Math.max(0.0002, sk.doDay), t + tanLen);
      gain.gain.setValueAtTime(Math.max(0.0002, sk.doDay), t + sk.thoiLuong * 0.65);
      gain.gain.linearRampToValueAtTime(0.0001, t + sk.thoiLuong);
    } else {
      const tanCong = Math.min(0.02, sk.thoiLuong * 0.2);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, sk.doDay), t + tanCong);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + sk.thoiLuong);
    }
    nguon.connect(loc);
    loc.connect(gain);
    gain.connect(ctx.destination);
    nguon.start(t);
    nguon.stop(t + sk.thoiLuong + 0.05);
  }

  /** Bộ đệm nhiễu trắng 2s, sinh deterministic (cùng seed mọi phiên). */
  private layBoDemNhieu(ctx: AudioContext): AudioBuffer {
    if (!this.boDemNhieu) {
      const doDai = Math.floor(ctx.sampleRate * 2);
      const boDem = ctx.createBuffer(1, doDai, ctx.sampleRate);
      const kenh = boDem.getChannelData(0);
      const rng = taoRng(777);
      for (let i = 0; i < doDai; i++) kenh[i] = rng() * 2 - 1;
      this.boDemNhieu = boDem;
    }
    return this.boDemNhieu;
  }
}
